import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// PayPal生产环境Client ID
const PAYPAL_CLIENT_ID = "AfA3baqZZItqHXDl2CK-fN2RtBouzkqgolyYn6OZALfcILPq1Kr7_J3qCnNKoV9ZL7v0T_hS4kffCpD1";

/**
 * 直接集成PayPal按钮组件 - 修复版本
 * 使用PayPal JavaScript SDK直接在前端创建和处理支付
 * 修复容器不存在问题
 */
const DirectPayPalButton = ({ shippingCost = 0, customStyle, returnUrl, customButtonText }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sdkReady, setSdkReady] = useState(false);
  const containerRef = useRef(null);
  const { clearCart, items, totalAmount } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // 计算订单总金额
  const orderTotal = parseFloat(totalAmount) + parseFloat(shippingCost);

  // 跟踪组件是否已卸载
  const isMounted = useRef(true);
  
  // 组件挂载和卸载管理
  useEffect(() => {
    // 组件挂载时设置标志
    isMounted.current = true;
    
    // 检查是否有之前成功但未处理完成的订单
    const checkPendingOrders = () => {
      try {
        // 检查各种可能的订单存储位置
        const storageKeys = [
          'successful_paypal_order',  // 常规成功订单
          'paypal_pending_order',     // 待确认订单
          'failed_capture_order_id'   // 捕获失败但可能已处理的订单
        ];
        
        // 按优先级检查订单
        for (const key of storageKeys) {
          const savedOrderJson = localStorage.getItem(key);
          if (savedOrderJson) {
            try {
              const savedOrder = JSON.parse(savedOrderJson);
              console.log(`发现存储的PayPal订单 [${key}]:`, savedOrder);
              
              // 清除数据
              localStorage.removeItem(key);
              
              // 清空购物车并重定向到成功页面
              if (savedOrder.id) {
                clearCart();
                navigate('/checkout-success', {
                  state: {
                    orderId: savedOrder.id,
                    amount: savedOrder.amount || totalAmount,
                    fromPayPal: true,
                    recovered: true,
                    status: savedOrder.status || 'COMPLETED'
                  }
                });
                return true;
              }
            } catch (e) {
              console.error('解析保存的订单数据失败:', e);
              localStorage.removeItem(key);
            }
          }
        }
        
        // 检查是否有进行中但未完成的订单
        const pendingOrderId = localStorage.getItem('pending_paypal_order_id');
        if (pendingOrderId) {
          console.log('发现处理中的PayPal订单ID:', pendingOrderId);
          
          // 在开发阶段，我们可以简单处理这些订单 - 提示用户它们可能已经完成
          alert(`您有一个可能已完成的订单 (${pendingOrderId})。请查看您的PayPal账户确认支付状态。`);
          
          // 清除数据
          localStorage.removeItem('pending_paypal_order_id');
        }
        
        return false;
      } catch (err) {
        console.error('检查订单状态时出错:', err);
        return false;
      }
    };
    
    // 执行检查，仅当没有恢复订单时才继续常规初始化
    if (!checkPendingOrders()) {
      console.log('没有待处理的订单，正常初始化PayPal按钮');
    }
    
    // 组件卸载时设置标志
    return () => {
      isMounted.current = false;
      console.log('DirectPayPalButton组件已卸载');
    };
  }, [clearCart, navigate, totalAmount]);
  
  // 当SDK准备好且组件已挂载时，尝试渲染按钮
  useEffect(() => {
    if (sdkReady && isMounted.current) {
      // 使用短延迟，确保DOM已经完全渲染
      const timer = setTimeout(() => {
        if (isMounted.current) {
          renderPayPalButton();
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [sdkReady]);
  
  // 加载PayPal SDK
  useEffect(() => {
    // 防止多次加载或在组件卸载后加载
    if (!isMounted.current) return;
    
    const loadPayPalScript = () => {
      console.log('开始加载PayPal SDK');
      
      // 如果组件已经卸载，则不进行任何操作
      if (!isMounted.current) return;
      
      // 先检查全局是否已存在PayPal实例
      if (window.paypal && window.paypal.Buttons) {
        console.log('全局PayPal SDK已加载');
        setSdkReady(true);
        setLoading(false);
        return;
      }
      
      // 清除之前可能存在的脚本，避免重复
      const existingScript = document.getElementById('direct-paypal-script');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }

      // 创建新的脚本元素，包含必要的参数
      const script = document.createElement('script');
      script.id = 'direct-paypal-script';
      // 必须加入currency参数，确保和创建订单时使用的货币一致
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=SGD`;
      script.async = true;
      
      // 脚本加载成功处理
      script.onload = () => {
        // 如果组件已经卸载，则不进行任何操作
        if (!isMounted.current) return;
        
        console.log('PayPal SDK 加载成功');
        setSdkReady(true);
        setLoading(false);
        
        // SDK加载成功后延迟渲染按钮，确保DOM已更新
        setTimeout(() => {
          if (isMounted.current) {
            renderPayPalButton();
          }
        }, 200);
      };
      
      // 脚本加载失败处理
      script.onerror = (err) => {
        // 如果组件已经卸载，则不进行任何操作
        if (!isMounted.current) return;
        
        console.error('PayPal SDK 加载失败:', err);
        setError('无法加载PayPal支付系统，请稍后再试');
        setLoading(false);
      };
      
      // 添加脚本到文档
      document.body.appendChild(script);
    };
    
    loadPayPalScript();
    
    // 组件卸载时清理
    return () => {
      // 不移除全局脚本，只标记组件已卸载
      isMounted.current = false;
    };
  }, []);

  // 保存订单到Firestore
  const saveOrderToFirestore = async (orderData) => {
    if (!currentUser) {
      console.log('用户未登录，跳过保存订单');
      return null;
    }
    
    try {
      // 准备订单数据
      const orderDoc = {
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          price: parseFloat(item.price),
          quantity: item.quantity,
          imageUrl: item.imageUrl || ''
        })),
        amount: {
          subtotal: totalAmount,
          shipping: shippingCost,
          total: orderTotal
        },
        payment: {
          method: 'paypal',
          id: orderData.id,
          status: orderData.status,
          createTime: new Date().toISOString(),
          paypalOrderData: orderData
        },
        status: orderData.status,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // 保存到Firestore
      const orderRef = await addDoc(collection(db, 'orders'), orderDoc);
      console.log('订单已保存:', orderRef.id);
      return orderRef.id;
    } catch (error) {
      console.error('保存订单失败:', error);
      return null;
    }
  };

  // 渲染PayPal按钮的关键函数
  const renderPayPalButton = () => {
    console.log('准备渲染PayPal按钮...');
    
    // 检查组件是否已卸载
    if (!isMounted.current) {
      console.log('组件已卸载，取消渲染PayPal按钮');
      return;
    }
    
    // 首先确认SDK已加载
    if (!window.paypal || !window.paypal.Buttons) {
      console.error('PayPal SDK未正确加载，无法渲染按钮');
      if (isMounted.current) {
        setError('PayPal SDK未正确加载，请刷新页面重试');
      }
      return;
    }
    
    // 然后检查容器DOM是否存在
    const container = containerRef.current;
    if (!container) {
      console.error('PayPal按钮容器不存在，跳过渲染');
      if (isMounted.current) {
        setError('无法找到PayPal按钮容器，请刷新页面重试');
      }
      return;
    }
    
    // 清空容器内容，防止重复渲染
    container.innerHTML = '';
    
    try {
      console.log('创建PayPal按钮配置...');
      
      // 创建PayPal按钮实例 - 使用简化的配置
      const paypalButtonInstance = window.paypal.Buttons({
        // 样式配置
        style: {
          color: 'gold',
          shape: 'rect',
          label: 'pay',
          height: 45
        },
        
        // 创建订单
        createOrder: (data, actions) => {
          console.log('创建PayPal订单...');
          
          // 准备商品数据
          const purchaseItems = items.map(item => ({
            name: item.name.substring(0, 120), // 确保名称不超过127个字符
            unit_amount: {
              currency_code: 'SGD',
              value: parseFloat(item.price).toFixed(2)
            },
            quantity: item.quantity.toString()
          }));
          
          // 创建订单
          return actions.order.create({
            intent: 'CAPTURE',
            purchase_units: [{
              amount: {
                currency_code: 'SGD',
                value: orderTotal.toFixed(2),
                breakdown: {
                  item_total: {
                    currency_code: 'SGD',
                    value: totalAmount.toFixed(2)
                  },
                  shipping: {
                    currency_code: 'SGD',
                    value: parseFloat(shippingCost).toFixed(2)
                  }
                }
              },
              items: purchaseItems
            }],
            application_context: {
              shipping_preference: 'NO_SHIPPING',
              user_action: 'PAY_NOW'
            }
          });
        },
        
        // 订单批准后 - 使用服务器端捕获逻辑
        onApprove: async (data, actions) => {
          console.log('PayPal订单已批准，准备捕获支付', data);
          setLoading(true);
          
          try {
            // 保存订单信息到localStorage，防止窗口意外关闭
            const orderId = data.orderID;
            localStorage.setItem('pending_paypal_order_id', orderId);
            
            // 设置更长的超时
            const captureTimeout = setTimeout(() => {
              if (isMounted.current) {
                console.warn('捕获订单超时，尝试服务器端查询');
                setError('处理时间较长，请稍等或刷新页面确认订单状态');
              }
            }, 15000); // 15秒超时警告
            
            try {
              // 首先尝试通过前端SDK捕获订单
              console.log('开始通过前端捕获PayPal订单...');
              
              // 由于可能出现"Window closed"错误，我们使用Promise.race与超时竞争
              const capturePromise = actions.order.capture();
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('前端捕获超时，切换到服务器端捕获')), 5000)
              );
              
              const captureResult = await Promise.race([capturePromise, timeoutPromise]);
              console.log('成功通过前端SDK捕获订单:', captureResult);
              
              // 前端捕获成功，处理订单
              clearTimeout(captureTimeout);
              
              // 检查组件是否仍然挂载
              if (!isMounted.current) {
                console.log('组件已卸载，但捕获已成功，保存订单信息到本地存储');
                localStorage.setItem('successful_paypal_order', JSON.stringify({
                  id: captureResult.id,
                  amount: orderTotal,
                  date: new Date().toISOString()
                }));
                return;
              }
              
              // 保存订单到Firestore
              await saveOrderToFirestore(captureResult);
              
              // 清除临时数据
              localStorage.removeItem('pending_paypal_order_id');
              
              // 清空购物车并导航到成功页面
              clearCart();
              navigate('/checkout-success', {
                state: {
                  orderId: captureResult.id,
                  amount: orderTotal,
                  fromPayPal: true
                }
              });
              
            } catch (frontendError) {
              // 前端捕获失败，切换到服务器端捕获
              console.warn('前端捕获失败，切换到服务器端捕获:', frontendError.message);
              
              try {
                // 使用Firebase服务端函数捕获订单
                if (!isMounted.current) {
                  console.log('组件已卸载，取消服务器端捕获');
                  return;
                }
                
                // 保存订单ID用于恢复
                localStorage.setItem('pending_paypal_order_id', orderId);
                
                console.log('前端捕获失败，切换到服务器端捕获');
                
                // 使用基础原始函数 - 指定app和region
                const functions = getFunctions(getApp(), 'asia-southeast1');
                
                // 尝试使用增强版V3函数，具有更好的错误处理
                console.log('尝试使用增强版capturePaypalOrderV3函数...');
                
                // 构建请求数据
                const requestData = {
                  orderId: orderId,
                  // 提供完整信息便于服务端记录
                  amount: orderTotal,
                  paymentType: 'product-cart',
                  source: 'DirectPayPalButton',
                  timestamp: Date.now(),
                  items: items.map(item => ({
                    id: item.id,
                    name: item.name,
                    price: parseFloat(item.price),
                    quantity: item.quantity
                  }))
                };
                
                let serverCaptureResult;
                
                // 先尝试使用增强版，如果失败则降级到标准版
                try {
                  const enhancedCaptureFunc = httpsCallable(functions, 'capturePaypalOrderV3');
                  console.log('调用增强版capturePaypalOrderV3函数处理订单:', orderId);
                  serverCaptureResult = await enhancedCaptureFunc(requestData);
                } catch (enhancedError) {
                  console.log('增强版函数调用失败，降级到标准capturePaypalOrder函数:', enhancedError.message);
                  try {
                    const standardCaptureFunc = httpsCallable(functions, 'capturePaypalOrder');
                    console.log('调用标准capturePaypalOrder函数处理订单:', orderId);
                    serverCaptureResult = await standardCaptureFunc(requestData);
                  } catch (standardError) {
                    console.error('标准捕获函数调用也失败:', standardError.message);
                    throw standardError; // 重新抛出错误以便外层catch处理
                  }
                }
                
                console.log('服务器端捕获成功:', serverCaptureResult.data);
                
                // 清除超时
                clearTimeout(captureTimeout);
                
                // 检查组件是否仍然挂载
                if (!isMounted.current) {
                  console.log('组件已卸载，但服务器端捕获已成功');
                  localStorage.setItem('successful_paypal_order', JSON.stringify({
                    id: orderId,
                    amount: orderTotal,
                    date: new Date().toISOString(),
                    serverCaptured: true
                  }));
                  return;
                }
                
                // 从服务器响应中获取订单详情
                const captureData = serverCaptureResult.data.captureDetails || {
                  id: orderId,
                  status: 'COMPLETED'
                };
                
                // 保存订单到Firestore
                await saveOrderToFirestore(captureData);
                
                // 清除临时数据
                localStorage.removeItem('pending_paypal_order_id');
                
                // 清空购物车并导航到成功页面
                clearCart();
                navigate('/checkout-success', {
                  state: {
                    orderId: captureData.id || orderId,
                    amount: orderTotal,
                    fromPayPal: true,
                    serverCaptured: true
                  }
                });
                
              } catch (serverError) {
                console.error('服务器端捕获也失败:', serverError);
                
                // 两种方法都失败了，但订单可能已经在PayPal端处理完成
                // 保存订单ID，以便用户可以稍后查询
                localStorage.setItem('failed_capture_order_id', orderId);
                
                if (isMounted.current) {
                  setError('支付可能已完成，但处理过程中断。请刷新页面或查看订单历史。');
                  setLoading(false);
                }
              }
            }
            
          } catch (error) {
            console.error('整个支付过程失败:', error);
            
            // 检查是否为窗口关闭错误
            const isConnectionError = 
              error.message?.includes('connection') || 
              error.message?.includes('Receiving end') ||
              error.message?.includes('Window closed') || 
              error.message?.includes('closed window');
              
            if (isConnectionError) {
              setError('连接中断，订单状态未知。请稍后刷新页面查看订单状态。');
            } else {
              setError(`支付处理失败: ${error.message || '未知错误'}`);
            }
            
            if (isMounted.current) {
              setLoading(false);
            }
          }
        },
        
        // 取消
        onCancel: () => {
          console.log('用户取消了PayPal支付');
          setLoading(false);
        },
        
        // 错误处理
        onError: (err) => {
          console.error('PayPal错误:', err);
          setError(`PayPal错误: ${err.message || '支付处理时发生错误'}`);
          setLoading(false);
        }
      });
      
      // 直接渲染到DOM容器元素
      console.log('开始渲染PayPal按钮到DOM容器...');
      
      // 再次检查组件是否已卸载
      if (!isMounted.current) {
        console.log('组件已卸载，取消PayPal按钮渲染');
        return;
      }
      
      try {
        // 使用try-catch包装渲染调用，避免未捕获的异常
        paypalButtonInstance.render(container)
          .then(() => {
            // 检查组件是否已卸载
            if (!isMounted.current) return;
            console.log('PayPal按钮渲染成功');
          })
          .catch(renderError => {
            // 检查组件是否已卸载
            if (!isMounted.current) return;
            console.error('PayPal按钮渲染失败:', renderError);
            setError(`PayPal按钮渲染失败: ${renderError.message || '未知错误'}`);
          });
      } catch (renderErr) {
        // 如果render方法本身抛出异常
        if (!isMounted.current) return;
        console.error('调用PayPal渲染方法失败:', renderErr);
        setError(`调用PayPal渲染方法失败: ${renderErr.message || '未知错误'}`);
      }
        
    } catch (err) {
      // 只有在组件仍然挂载时才设置错误状态
      if (!isMounted.current) return;
      console.error('渲染PayPal按钮过程中出错:', err);
      setError(`渲染PayPal按钮失败: ${err.message || '未知错误'}`);
      setLoading(false);
    }
  };
  
  // 检查是否可以显示PayPal按钮
  const canShowButton = items.length > 0 && orderTotal > 0;

  // 组件正在加载
  if (loading && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          正在加载PayPal支付...
        </Typography>
      </Box>
    );
  }

  // 显示错误
  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 2 }}
        action={
          <Box component="button" 
            onClick={() => {
              setError('');
              setLoading(true);
              setTimeout(renderPayPalButton, 500);
            }}
            sx={{ 
              border: 'none', 
              background: 'none', 
              cursor: 'pointer',
              color: 'error.main',
              textDecoration: 'underline'
            }}
          >
            重试
          </Box>
        }
      >
        {error}
        <Typography variant="body2" sx={{ mt: 1 }}>
          请刷新页面重试，或选择其他支付方式。
        </Typography>
      </Alert>
    );
  }

  if (!canShowButton) {
    return (
      <Alert severity="info">
        购物车为空或总金额为0，无法显示支付按钮
      </Alert>
    );
  }

  return (
    <Box sx={{ ...customStyle }}>
      {canShowButton && !sdkReady ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            正在准备PayPal支付...
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* 固定的容器使用ref，不再动态生成ID */}
          <div 
            id="direct-paypal-button-container" 
            ref={containerRef}
            style={{ width: '100%', minHeight: '45px' }}
          />
          
          <Typography 
            variant="caption" 
            sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}
          >
            点击上方按钮使用PayPal安全支付 {orderTotal > 0 ? `S$${orderTotal.toFixed(2)}` : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DirectPayPalButton;